/**
 * Projects Query Hooks
 * React Query hooks for projects data fetching and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsService } from 'src/api/services';
import { useAuth } from 'src/Contexts/AuthContext';
import { useToast } from 'src/Contexts/ToastContext';
import { queryKeys } from './queryKeys';
import type { Project } from 'src/types';

/**
 * Hook to fetch projects for the current logged-in user.
 * Uses GET /user/me → project_ids → fetch projects by IDs.
 */
export function useProjectsQuery() {
  const { currentUser } = useAuth();

  return useQuery({
    queryKey: queryKeys.projects.user(currentUser?.email || ''),
    queryFn: async () => {
      if (!currentUser?.email) return [];
      return await projectsService.getUserProjects();
    },
    enabled: !!currentUser?.email,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch all projects
 * Used in management/admin interfaces
 */
export function useAllProjectsQuery() {
  return useQuery({
    queryKey: queryKeys.projects.lists(),
    queryFn: () => projectsService.getAll(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch a single project by ID
 */
export function useProjectQuery(id: string) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => projectsService.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation hook to create a new project
 */
export function useCreateProjectMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: Partial<Project>) => projectsService.create(data),

    onMutate: async (newProject) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projects.all });

      const previousProjects = queryClient.getQueryData(queryKeys.projects.lists());

      queryClient.setQueryData<Project[]>(queryKeys.projects.lists(), (old = []) => [
        ...old,
        { ...newProject, id: `temp-${Date.now()}` } as Project,
      ]);

      return { previousProjects };
    },

    onError: (err, _newProject, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKeys.projects.lists(), context.previousProjects);
      }
      toast.error('Failed to create project');
      console.error('Create project error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      toast.success('Project created successfully');
    },
  });
}

/**
 * Mutation hook to update an existing project
 */
export function useUpdateProjectMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) =>
      projectsService.update(id, data),

    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projects.all });

      const previousProjects = queryClient.getQueryData(queryKeys.projects.lists());

      queryClient.setQueryData<Project[]>(queryKeys.projects.lists(), (old = []) =>
        old.map((project) => (project.id === id ? { ...project, ...data } : project))
      );

      queryClient.setQueryData<Project>(queryKeys.projects.detail(id), (old) =>
        old ? { ...old, ...data } : old
      );

      return { previousProjects };
    },

    onError: (err, { id: _id }, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKeys.projects.lists(), context.previousProjects);
      }
      toast.error('Failed to update project');
      console.error('Update project error:', err);
    },

    onSuccess: (_, { id: _id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      toast.success('Project updated successfully');
    },
  });
}

/**
 * Mutation hook to delete a project
 */
export function useDeleteProjectMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => projectsService.delete(id),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projects.all });

      const previousProjects = queryClient.getQueryData(queryKeys.projects.lists());

      queryClient.setQueryData<Project[]>(queryKeys.projects.lists(), (old = []) =>
        old.filter((project) => project.id !== id)
      );

      return { previousProjects };
    },

    onError: (err, _id, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(queryKeys.projects.lists(), context.previousProjects);
      }
      toast.error('Failed to delete project');
      console.error('Delete project error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      toast.success('Project deleted successfully');
    },
  });
}
